/**
 * Reine Best-Effort-Heuristik, um Betrag/Datum/Brutto-Netto/Firmenhinweis aus dem
 * TEXT eines Dividenden-Belegs (PDF-Abrechnung von Bank/Broker) zu erraten (backlog.md
 * -> "optionaler PDF-Upload/OCR", client-seitig). Kein echtes Bild-OCR (deckt nur PDFs
 * mit echter Textebene ab, wie die allermeisten computererzeugten Bank-/Broker-
 * Abrechnungen - kein gescanntes Papier) und kein Ersatz fuer die Bestaetigung durch
 * den Nutzer: liefert nur einen VORSCHLAG, der die Formularfelder vorbefuellt, aber
 * immer vor dem Speichern ueberprueft/korrigiert werden kann/muss (nie eine geratene
 * Aktie automatisch auswaehlen - siehe `companyGuess`-Kommentar unten).
 */
import { parseFlexibleDate, parseFlexibleNumber } from "@/lib/portfolio/import/csv-parsing";
import { isValidIsinChecksum } from "@/lib/isin";

export interface DividendReceiptGuess {
  amount?: number;
  currency?: string;
  paymentDate?: string;
  isGross?: boolean;
  /** Nur ein Rohtext-Hinweis (z.B. ein erkannter Firmenname/Ticker-artiges Token) fuer
   * die Suchleiste - wird NIE automatisch zu einer ausgewaehlten Aktie, da eine falsche
   * automatische Zuordnung (z.B. bei mehrdeutigen Firmennamen) unbemerkt zu einer
   * falschen Dividenden-Zuordnung fuehren koennte. Der Nutzer waehlt immer selbst aus
   * den echten Suchvorschlaegen. */
  companyGuess?: string;
  /** ISIN, falls im Text ein exaktes 12-stelliges Muster gefunden wurde (Feedback-Runde
   * 19 C/13, 2026-07-28: Recherche zu Trade-Republic-Belegen zeigt, europaeische
   * Broker-Abrechnungen referenzieren Wertpapiere durchgehend per ISIN, nicht per
   * US-Ticker - siehe `classifyQuery()` in lib/data/classify-query.ts, das ISIN bereits
   * als eigenen, verlaesslich erkennbaren Abfrage-Typ kennt). Deutlich zuverlaessiger als
   * `companyGuess` (dessen lockeres "2-5 Grossbuchstaben"-Muster auf jedem Beleg leicht
   * falsch treffen kann, z.B. auf "ISIN" selbst oder den Bank-/Broker-Namen) - wird vom
   * Aufrufer bevorzugt verwendet, falls vorhanden. */
  isin?: string;
}

const CURRENCY_SYMBOLS: Record<string, string> = { "€": "EUR", "$": "USD", "£": "GBP" };

// [KRITISCH, Punkt 16] 2026-08-11: "betrag" (generisches Catch-all) entfernt und "gesamt"
// ergaenzt. Grund: "betrag" ist Teilstring von JEDEM "...steuerbetrag"/"Steuerbetrag"-Label
// (z.B. "Quellensteuerbetrag") und konnte dadurch eine Steuerzeile faelschlich genauso gut
// wie "Bruttobetrag" scoren. "Gesamt" fehlte dagegen komplett - viele Broker-Abrechnungen
// (u.a. Trade Republic) beschriften den tatsaechlichen Auszahlungsbetrag nur mit "Gesamt",
// nicht "Gesamtbetrag" - dieser Betrag hatte dadurch bisher gar keinen Keyword-Treffer und
// verlor gegen zufaellig naeher liegende, staerker verankerte Konkurrenten.
// Broker-Recherche 2026-08-19 (backlog.md -> [Punkt 16], "weitere Broker" - Rest von
// Scalable Capital/Comdirect/ING/DKB): ueber reale, von Nutzern im Portfolio-Performance-
// Forum geteilte PDF-Textausschnitte verifiziert (keine geratenen Formate, siehe
// Null-Fehler-Toleranz-Vorgabe). Ergebnis: "gesamt"/"gesamtbetrag" deckt bereits Scalable
// Capital ("Gesamtbetrag") UND ING ("Gesamtbetrag zu Ihren Gunsten EUR ...") ab, kein
// neues Keyword noetig. "zu ihren gunsten" neu ergaenzt fuer Comdirect ("Zu Ihren Gunsten
// vor Steuern: EUR ...") - dieselbe Formulierung kommt bei ING als Teil von
// "Gesamtbetrag zu Ihren Gunsten" ebenfalls vor, also ein echtes, mehrfach bestaetigtes
// Muster, kein Einzelfall. DKB-spezifische Dividenden-Belegformulierungen konnten in der
// Recherche NICHT konkret verifiziert werden (nur artfremde Wertpapierabrechnungs-/
// Kontoauszug-Dokumente mit Feldern wie "Ausmachender Betrag" gefunden) - bewusst NICHT
// blind ergaenzt, siehe backlog.md fuer die offene Bitte um einen echten DKB-Beispielbeleg.
const AMOUNT_KEYWORDS = [
  "bruttobetrag",
  "dividendenbetrag",
  "auszahlungsbetrag",
  "zahlbetrag",
  "nettobetrag",
  "gesamtbetrag",
  "gesamt",
  "dividende",
  "zu ihren gunsten",
];

// [KRITISCH, Punkt 16] 2026-08-11: Steuer-Abzugspositionen (Quellensteuer, Kapitalertrag-
// steuer, Abgeltungsteuer, Soli...) sollen NIE als der eigentliche Dividendenbetrag gewaehlt
// werden - unabhaengig davon, ob sie zufaellig auch ein AMOUNT_KEYWORDS-Wort in der Naehe
// haben.
// KORRIGIERT (Broker-Recherche 2026-08-19, eigener Regressionstest deckte den Bug auf):
// vom generischen "steuer"-Teilstring auf konkrete Steuer-Komposita umgestellt. Grund: das
// neu ergaenzte Comdirect-Muster "Zu Ihren Gunsten VOR STEUERN" (Bruttobetrag-Marker,
// siehe GROSS_KEYWORDS) enthaelt selbst "steuer" als Teilstring von "Steuern" - der vorher
// generische Ausschluss verwarf dadurch faelschlich genau den Betrag, den er eigentlich
// durchlassen sollte. Reale Steuer-ABZUGSPOSITIONEN sind dagegen immer eigene Komposita
// (Quellensteuer, Kapitalertragsteuer, Abgeltungsteuer, Kirchensteuer, Steuerbetrag), nie
// "vor/nach steuern" - explizite Liste statt Teilstring schliesst diese Kollision aus, ohne
// einen der bekannten Steuer-Begriffe zu verlieren. "qust" ergaenzt: ING kuerzt
// Quellensteuer auf Dividenden-Belegen als "QuSt" ab (z.B. "QuSt 15,00 % (EUR 1,58) USD
// 1,94").
const TAX_EXCLUSION_KEYWORDS = [
  "quellensteuer",
  "kapitalertragsteuer",
  "abgeltungsteuer",
  "kirchensteuer",
  "steuerbetrag",
  "solidaritätszuschlag",
  "qust",
];

const DATE_KEYWORDS = ["zahlbarkeitstag", "zahltag", "zahlungstag", "valuta", "auszahlungsdatum", "datum"];

// "vor steuern" ergaenzt (Broker-Recherche 2026-08-19, Comdirect: "Zu Ihren Gunsten vor
// Steuern" markiert eindeutig den Brutto-Betrag) - zusaetzlich zum bereits bestehenden
// "brutto", nicht als Ersatz.
const GROSS_KEYWORDS = ["brutto", "vor steuern"];
const NET_KEYWORDS = ["netto"];

/** Alle Zahl+Waehrungs-Vorkommen im Text mit ihrer Zeilen-Position, damit die
 * Naehe zu einem Schluesselwort bewertet werden kann.
 *
 * Drittes Muster ergaenzt (Broker-Recherche 2026-08-19): Comdirect/ING schreiben den
 * Waehrungscode als eigenes Wort VOR die Zahl ("EUR 5,57", "USD 12,90"), nicht als Symbol
 * direkt davor oder als Code danach (die bisherigen zwei Muster, aus Trade-Republic-
 * Belegen "12,50 EUR" abgeleitet). Nutzt ein Lookbehind statt einer dritten normalen
 * Erfassungsgruppe, damit `match.index` wie bei den anderen beiden Mustern auf die ZAHL
 * zeigt (nicht auf den Waehrungscode davor) - sonst waere die Fensterposition fuer
 * `keywordProximityScore()` je nach Muster unterschiedlich verschoben. */
function findAmountCandidates(text: string): { value: number; currency: string; index: number }[] {
  const pattern =
    /(?:([€$£])\s?(\d{1,3}(?:[.,]\d{3})*[.,]\d{2}))|(?:(\d{1,3}(?:[.,]\d{3})*[.,]\d{2})\s?(EUR|USD|GBP|€|\$|£))|(?:(?<=\b(EUR|USD|GBP)\s)(\d{1,3}(?:[.,]\d{3})*[.,]\d{2}))/gi;
  const candidates: { value: number; currency: string; index: number }[] = [];
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    const rawSymbolFirst = match[1];
    const rawNumberFirst = match[2];
    const rawNumberSecond = match[3];
    const rawSymbolSecond = match[4];
    const rawCurrencyThird = match[5];
    const rawNumberThird = match[6];
    const rawNumber = rawNumberFirst ?? rawNumberSecond ?? rawNumberThird;
    const rawSymbol = rawSymbolFirst ?? rawSymbolSecond ?? rawCurrencyThird;
    const value = rawNumber ? parseFlexibleNumber(rawNumber) : undefined;
    if (value === undefined || !rawSymbol) continue;
    const currency = CURRENCY_SYMBOLS[rawSymbol] ?? rawSymbol.toUpperCase();
    candidates.push({ value, currency, index: match.index });
  }
  return candidates;
}

/**
 * [KRITISCH, Punkt 16] 2026-08-11: von START- auf ENDE-des-Keywords verankerte Distanz
 * umgestellt. Vorher (`window.length - i`, i = Startindex des Treffers) hing die Distanz
 * an der LAENGE des Keyword-Worts selbst, nicht an der echten Luecke zwischen Label und
 * Zahl - "Nettobetrag" (11 Zeichen) schlug dadurch bei sonst identischer Formatierung
 * IMMER "Bruttobetrag" (12 Zeichen), nur weil es ein Zeichen kuerzer ist, obwohl beide
 * Label ihrer Zahl gleich nah stehen. Gefunden beim Debuggen des Trade-Republic-Bugfixes
 * oben, als das Entfernen des generischen "betrag"-Keywords genau diesen latenten Bug
 * freilegte (vorher ueberdeckte "betrag" als gemeinsamer kuerzerer Treffer die Differenz).
 */
function keywordProximityScore(text: string, index: number, keywords: string[]): number {
  const windowStart = Math.max(0, index - 60);
  const window = text.slice(windowStart, index).toLowerCase();
  const distances = keywords
    .map((k) => {
      const i = window.lastIndexOf(k);
      return i === -1 ? undefined : window.length - (i + k.length);
    })
    .filter((d): d is number => d !== undefined);
  return distances.length > 0 ? Math.min(...distances) : Infinity;
}

/**
 * Extrahiert einen Best-Effort-Vorschlag aus dem rohen PDF-Text. Reine Funktion (kein
 * I/O) - die eigentliche PDF-Text-Extraktion passiert davor in extract-pdf-text.ts.
 */
export function parseDividendReceiptText(text: string): DividendReceiptGuess {
  const amountCandidates = findAmountCandidates(text);
  let bestAmount: { value: number; currency: string; index: number } | undefined;
  let bestScore = Infinity;
  for (const candidate of amountCandidates) {
    const score = keywordProximityScore(text, candidate.index, AMOUNT_KEYWORDS);
    // [KRITISCH, Punkt 16] 2026-08-11: liegt ein Steuer-Schluesselwort (z.B. "Quellensteuer")
    // NAEHER am Betrag als jedes AMOUNT_KEYWORDS-Wort, ist das eine Steuer-Abzugsposition,
    // kein Dividendenbetrag - Kandidat verwerfen, selbst wenn er (z.B. ueber ein "...steuer-
    // BETRAG"-Kompositum) ebenfalls irgendeinen Score haette. Behebt den gemeldeten Bug, bei
    // dem Trade-Republic-Belege die US-Quellensteuer statt des "Gesamt"-Betrags auswiesen.
    const taxScore = keywordProximityScore(text, candidate.index, TAX_EXCLUSION_KEYWORDS);
    if (taxScore < score) continue;
    if (score < bestScore) {
      bestScore = score;
      bestAmount = candidate;
    }
  }
  // Ohne jeden Keyword-Treffer den groessten Betrag annehmen (Dividendenbelege haben
  // selten viele verschiedene Betraege, der groesste ist meist der Hauptbetrag, kleinere
  // oft Steuer-Teilbetraege) - immer noch nur ein Vorschlag, kein Endergebnis.
  if (!bestAmount && amountCandidates.length > 0) {
    bestAmount = amountCandidates.reduce((max, c) => (c.value > max.value ? c : max));
  }

  // Ein Dividenden-BELEG bestaetigt immer eine bereits erfolgte/anstehende Zahlung -
  // ein Datum in der Zukunft kann daher nie das gesuchte Zahlungsdatum sein (eher ein
  // Dokumentendatum/eine andere Nebenangabe) und wird schon hier ausgeschlossen, statt
  // erst im Fallback ohne Keyword-Treffer - sonst koennte ein zufaellig nahes, aber
  // falsches Keyword ein Zukunftsdatum faelschlich bevorzugen.
  const today = new Date().toISOString().slice(0, 10);
  const datePattern = /\b(\d{2}\.\d{2}\.\d{4}|\d{4}-\d{2}-\d{2})\b/g;
  const dateCandidates: { value: string; index: number }[] = [];
  let dateMatch: RegExpExecArray | null;
  while ((dateMatch = datePattern.exec(text)) !== null) {
    const parsed = parseFlexibleDate(dateMatch[1]);
    if (parsed && parsed <= today) dateCandidates.push({ value: parsed, index: dateMatch.index });
  }
  let bestDate: { value: string; index: number } | undefined;
  let bestDateScore = Infinity;
  for (const candidate of dateCandidates) {
    const score = keywordProximityScore(text, candidate.index, DATE_KEYWORDS);
    if (score < bestDateScore) {
      bestDateScore = score;
      bestDate = candidate;
    }
  }
  // Fallback ohne jeden Keyword-Treffer: das juengste plausible Datum im Text.
  if (!bestDate && dateCandidates.length > 0) {
    bestDate = [...dateCandidates].sort((a, b) => a.value.localeCompare(b.value)).at(-1);
  }

  let isGross: boolean | undefined;
  if (bestAmount) {
    const grossScore = keywordProximityScore(text, bestAmount.index, GROSS_KEYWORDS);
    const netScore = keywordProximityScore(text, bestAmount.index, NET_KEYWORDS);
    if (grossScore !== Infinity || netScore !== Infinity) isGross = grossScore < netScore;
  }

  // Firmen-/Ticker-Hinweis: erstes grossgeschriebenes 2-5-Buchstaben-Token (typisches
  // Ticker-Muster) - bewusst kein Versuch, echte Firmennamen zu erkennen (zu
  // fehleranfaellig ohne echtes NLP), reicht als Suchleisten-Vorbefuellung.
  const tickerMatch = /\b([A-Z]{2,5}(?:\.[A-Z]{1,2})?)\b/.exec(text);

  // ISIN (Feedback-Runde 19 C/13, 2026-07-28): deutlich zuverlaessiger als der lockere
  // Ticker-Match oben - europaeische Broker-Abrechnungen (u.a. Trade Republic)
  // referenzieren Wertpapiere durchgehend per ISIN. Zusaetzlich per Pruefziffer (ISO 6166,
  // Luhn-Algorithmus auf die in Ziffern uebersetzten Buchstaben) validiert statt jedes
  // zufaellig 12-stellige alphanumerische Token zu uebernehmen (z.B. eine
  // Belegnummer) - deutlich weniger falsch-positiv als der reine Formatabgleich allein.
  const isinCandidates = text.match(/\b[A-Z]{2}[A-Z0-9]{9}\d\b/g) ?? [];
  const isin = isinCandidates.find(isValidIsinChecksum);

  return {
    amount: bestAmount?.value,
    currency: bestAmount?.currency,
    paymentDate: bestDate?.value,
    isGross,
    isin,
    companyGuess: tickerMatch?.[1],
  };
}
